import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  DureeBailContexte,
  DureeBailUnite,
  dureeBailDefaut,
  dureeBailErrorKey,
  dureeBailHintKey,
  formatDureeBailDto,
  isDureeBailValide,
} from '../../core/models/locataire-bail.model';
import { Locataire } from '../../core/models/locataire.model';
import {
  formatLogementChoiceLabel,
  isParking,
  isUniteFermee,
  Logement,
} from '../../core/models/logement.model';

export type LocataireFormControls = {
  civilite: string;
  nom: string;
  prenom: string;
  telephone: string;
  mail: string;
  dateNaissance: string;
  villeNaissance: string;
  logementId: string;
  dateEntree: string;
  dateBail: string;
  dateCles: string;
  etudiant: boolean;
  dureeValeur: number;
  dureeUnite: DureeBailUnite;
};

export function createLocataireForm(
  fb: FormBuilder,
  getSelectedMeuble: () => boolean
) {
  return fb.nonNullable.group(
    {
      civilite: [''],
      nom: ['', Validators.required],
      prenom: ['', Validators.required],
      telephone: ['', Validators.required],
      mail: [''],
      dateNaissance: [''],
      villeNaissance: [''],
      logementId: ['', Validators.required],
      dateEntree: [''],
      dateBail: [''],
      dateCles: [''],
      etudiant: [false],
      dureeValeur: [3, [Validators.required, Validators.min(1)]],
      dureeUnite: ['ans' as DureeBailUnite, Validators.required],
    },
    { validators: [dureeBailMinValidator(getSelectedMeuble)] }
  );
}

export function dureeBailMinValidator(
  getSelectedMeuble: () => boolean
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const valeur = Number(control.get('dureeValeur')?.value);
    const unite = control.get('dureeUnite')?.value as DureeBailUnite;
    const etudiant = !!control.get('etudiant')?.value;
    if (!unite || !Number.isFinite(valeur)) {
      return null;
    }
    return isDureeBailValide(valeur, unite, {
      meuble: getSelectedMeuble(),
      etudiant,
    })
      ? null
      : { dureeBailMin: true };
  };
}

export function dureeBailFormContexte(
  form: FormGroup,
  selectedMeuble: boolean
): DureeBailContexte {
  return {
    meuble: selectedMeuble,
    etudiant: !!form.get('etudiant')?.value,
  };
}

export function dureeBailFormHintKey(
  form: FormGroup,
  selectedMeuble: boolean
): string {
  return dureeBailHintKey(dureeBailFormContexte(form, selectedMeuble));
}

export function dureeBailFormErrorKey(
  form: FormGroup,
  selectedMeuble: boolean
): string {
  return dureeBailErrorKey(dureeBailFormContexte(form, selectedMeuble));
}

export function isDureeBailFormInvalid(form: FormGroup): boolean {
  return (
    form.hasError('dureeBailMin') &&
    (!!form.get('dureeValeur')?.touched ||
      !!form.get('dureeUnite')?.touched ||
      form.touched)
  );
}

export function resolveLogementMeuble(
  logement: Logement | undefined
): boolean {
  if (!logement || isParking(logement.typeUniteDto)) {
    return false;
  }
  return !!logement.meubleDto;
}

export function applyDureeBailDefaut(
  form: FormGroup,
  logements: Logement[],
  fallbackLogement?: Logement
): boolean {
  const logementId = form.get('logementId')?.value as string;
  let selectedMeuble = false;
  if (logementId) {
    const logement =
      logements.find((l) => l.idLogementDto === logementId) ?? fallbackLogement;
    selectedMeuble = resolveLogementMeuble(logement);
  }

  const defaut = dureeBailDefaut(
    dureeBailFormContexte(form, selectedMeuble)
  );
  form.patchValue(
    {
      dureeValeur: defaut.valeur,
      dureeUnite: defaut.unite,
    },
    { emitEvent: false }
  );
  form.get('dureeValeur')?.markAsUntouched();
  form.get('dureeUnite')?.markAsUntouched();
  form.updateValueAndValidity({ emitEvent: false });
  return selectedMeuble;
}

export function logementOptionLabel(
  logement: Logement,
  translate: (key: string) => string
): string {
  return formatLogementChoiceLabel(logement, translate);
}

export function filterOpenLogements(
  logements: Logement[],
  translate: (key: string) => string
): Logement[] {
  return (logements ?? [])
    .filter((logement) => !!logement.idLogementDto && !isUniteFermee(logement))
    .sort((a, b) =>
      logementOptionLabel(a, translate).localeCompare(
        logementOptionLabel(b, translate),
        'fr'
      )
    );
}

export function locataireFromFormValue(
  value: LocataireFormControls,
  extras?: {
    idLocataireDto?: string;
    dateSortieDto?: Date | string;
    activeDto?: boolean;
    idContactDto?: string;
  }
): Locataire {
  const locataire = new Locataire();
  if (extras?.idLocataireDto) {
    locataire.idLocataireDto = extras.idLocataireDto;
  }
  locataire.civiliteDto = value.civilite.trim() || undefined;
  locataire.nomDto = value.nom.trim();
  locataire.prenomDto = value.prenom.trim();
  locataire.telephoneDto = value.telephone.trim();
  locataire.mailDto = value.mail.trim() || undefined;
  locataire.dateNaissanceDto = value.dateNaissance || undefined;
  locataire.villeNaissanceDto = value.villeNaissance.trim() || undefined;
  locataire.dateEntreeDto = value.dateEntree || undefined;
  locataire.dateBailDto = value.dateBail || undefined;
  locataire.dateClesDto = value.dateCles || undefined;
  if (extras?.dateSortieDto !== undefined) {
    locataire.dateSortieDto = extras.dateSortieDto;
  }
  locataire.dureeBailDto = formatDureeBailDto(
    Number(value.dureeValeur),
    value.dureeUnite
  );
  locataire.etudiantDto = !!value.etudiant;
  locataire.activeDto = extras?.activeDto ?? true;
  if (extras?.idContactDto !== undefined) {
    locataire.idContactDto = extras.idContactDto;
  }

  const logement = new Logement();
  logement.idLogementDto = value.logementId;
  locataire.logementDto = logement;
  return locataire;
}
